/**
 * ONE ENQUIRY ON A PHONE. The whole row, and both forms inside it, with the latch that keeps a
 * folded form from painting anything before somebody opens it.
 */
import { useState, type ReactNode } from 'react';
import { SERVICE_LINE_LABELS } from '@fk/shared';
import type { EnquiryRow, Me, OfficeUser } from '../../api.ts';
import { Card, Detail, Pill } from '../../components/AppShell.tsx';
import { CallLink, MailLink } from '../../components/CallLink.tsx';
import { MarkPills } from '../../components/DoNotContact.tsx';
import { OpenTheCall } from '../../components/OpenTheCall.tsx';
import { GapPill } from '../../components/StatusPills.tsx';
import { formatInstant } from '../../format.ts';
import { CHANNEL_LABELS, ENQUIRY_STATE_LABELS } from '../../labels.ts';
import { openEnquiry } from '../../source.ts';
import { TextThread } from '../TextThread.tsx';
import { MakeClientForm } from './MakeClientForm.tsx';
import { NextAction, Owner, SheIsAHousehold } from './RowWords.tsx';
import { WhereSheCameFrom, sourceKey } from './WhereSheCameFrom.tsx';
import { WorkItForm } from './WorkItForm.tsx';
import { becameAHousehold, heldBackWords, stateTone } from './rows.ts';

export function EnquiryCard({
  row,
  today,
  me,
  people,
  onChanged,
  actions,
}: {
  row: EnquiryRow;
  today: string;
  me: Me;
  people: readonly OfficeUser[];
  onChanged: (sentence: string) => void;
  /** False from md up, where the same two forms are drawn once in the panel instead. */
  actions: boolean;
}) {
  const open = openEnquiry(row.state);
  const noReply = row.state === 'new' && row.firstResponseAt === null;
  const overdue = open && row.nextActionOn !== null && row.nextActionOn < today;
  const unowned = open && row.ownerUserId === null;
  const neglected = noReply || overdue || unowned;

  return (
    <Card className={neglected ? 'border-coral/40' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-serif text-lg leading-tight">{row.name}</h2>
        <Pill tone={stateTone(row.state)}>{ENQUIRY_STATE_LABELS[row.state]}</Pill>
        {noReply ? <GapPill>No reply yet</GapPill> : null}
        <MarkPills marks={row.doNotContact} />
      </div>

      <p className="mt-1 text-sm text-navy/75">
        {CHANNEL_LABELS[row.sourceChannel]}
        {row.serviceLine ? `, ${SERVICE_LINE_LABELS[row.serviceLine].toLowerCase()}` : ''}
        {row.sourceDetail ? (
          <span className="ml-2 text-xs text-navy/65">{row.sourceDetail}</span>
        ) : null}
      </p>
      {/* The same fact the desk reads in the Arrived column, on the line the other facts sit on,
          so a card and a row never disagree about when somebody asked. */}
      <p className="mt-0.5 text-xs text-navy/65">Arrived {formatInstant(row.createdAt)}</p>

      {row.phoneE164 || row.email ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {row.phoneE164 ? (
            <CallLink phoneE164={row.phoneE164} marked={row.doNotContact?.call ?? null} />
          ) : null}
          <MailLink email={row.email} />
        </div>
      ) : null}
      {/* The call that opened it, as the phone system tells it (ops #13). */}
      <OpenTheCall me={me} callSid={row.callSid} className="mt-1" />

      <div className="mt-3 space-y-1 text-sm">
        <Owner row={row} me={me} open={open} />
        <NextAction row={row} open={open} overdue={overdue} />
      </div>

      <dl className="mt-1">
        <Detail label="Town" value={[row.town, row.zip].filter(Boolean).join(' ') || null} />
        <Detail label="First reply" value={formatInstant(row.firstResponseAt)} />
        <Detail label="Booked" value={formatInstant(row.convertedAt)} />
        <Detail label="Held back by" value={heldBackWords(row)} />
        <Detail label="Lost because" value={row.lostReason} />
        <Detail label="Notes" value={row.notes} />
      </dl>

      {/*
       * Her texts on the enquiry (ops #26, part 2), opened rather than drawn: a list of missed calls
       * is thirty cards, and a thread on each would be thirty reads before anybody pressed anything.
       * From md up the panel draws them for the one row it is about.
       */}
      {actions ? (
        <Disclosure summary="Texts">
          <div className="mt-2">
            <TextThread of={{ kind: 'enquiry', id: row.id }} herName={row.name} />
          </div>
        </Disclosure>
      ) : null}

      {becameAHousehold(row) ? (
        <div className="mt-3">
          <SheIsAHousehold row={row} />
        </div>
      ) : actions ? (
        <>
          {/*
           * Both of these are opened rather than always drawn, because both can hold the source
           * block and its two hundred stored details: "Where she came from" always, and "Work it"
           * whenever nobody has answered the question on this row, which on a list of missed calls
           * is every card on the screen. `Disclosure` mounts on the first press and keeps it.
           */}
          <Disclosure summary="Work it">
            <WorkItForm idPrefix="card" row={row} me={me} people={people} onChanged={onChanged} />
          </Disclosure>
          <Disclosure summary="Where they came from">
            <WhereSheCameFrom
              key={sourceKey(row)}
              idPrefix="card"
              row={row}
              onChanged={onChanged}
            />
          </Disclosure>
          <details className="mt-3 rounded-lg border border-navy/15 p-3">
            <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
              Make them a client
            </summary>
            <MakeClientForm idPrefix="card" row={row} />
          </details>
        </>
      ) : null}
    </Card>
  );
}

/**
 * A block on a card that mounts nothing until somebody opens it, and never unmounts it again.
 *
 * Both halves matter and they pull opposite ways. Anything holding `SourceFields` carries a
 * datalist of every source detail already stored, up to two hundred of them, and a phone showing
 * thirty cards would paint thirty of those before anybody pressed anything. But a block that
 * unmounted when the card was folded up would throw away whatever was half typed in it, and
 * "Work it" holds the state, the next action and the notes. So the latch is one way: closed until
 * opened once, and from then on the browser hides it rather than React discarding it.
 */
function Disclosure({ summary, children }: { summary: string; children: ReactNode }) {
  const [opened, setOpened] = useState(false);
  return (
    <details
      className="mt-3 rounded-lg border border-navy/15 p-3"
      onToggle={(event) => {
        if (event.currentTarget.open) setOpened(true);
      }}
    >
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        {summary}
      </summary>
      {opened ? children : null}
    </details>
  );
}
